import { ChangeEvent, FC } from 'react';

import { Add, Delete, ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import type { UseReceivingDetailResult } from '../hooks/useReceivingDetail';

interface ListedItemsSectionProps {
  detail: UseReceivingDetailResult;
  poValidated: boolean;
}

const ListedItemsSection: FC<ListedItemsSectionProps> = ({ detail, poValidated }) => {
  const { lineItems, dirty, saving, saveLineItems, addLine, removeLine, updateLine, hasScan, loading } = detail;

  return (
    <Accordion defaultExpanded={false} style={{ marginBottom: 24 }}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="subtitle1">Listed Items (experimental)</Typography>
      </AccordionSummary>
      <AccordionDetails>
        {hasScan && (
          <Alert severity="warning" style={{ marginBottom: 12 }}>
            <strong>Read automatically from the photo &mdash; check it.</strong> Descriptions and quantities can be
            misread. Compare every line against the packing slip and correct anything wrong before saving.
          </Alert>
        )}

        {loading && <CircularProgress size={20} />}

        {lineItems.map((line, index) => (
          // Index-keyed deliberately: a new row has no id yet, and rows are only ever appended or
          // removed as a whole, never reordered, so the index is stable for a row's lifetime.
          // eslint-disable-next-line react/no-array-index-key
          <Grid container spacing={1} alignItems="center" key={index} style={{ marginBottom: 8 }}>
            <Grid item xs={8} sm={9}>
              <TextField
                fullWidth
                size="small"
                label="Description"
                value={line.description}
                disabled={!poValidated}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  updateLine(index, { description: event.target.value })
                }
              />
            </Grid>
            <Grid item xs={3} sm={2}>
              <TextField
                fullWidth
                size="small"
                label="Qty"
                value={line.quantity ?? ''}
                disabled={!poValidated}
                inputProps={{ inputMode: 'decimal' }}
                onChange={(event: ChangeEvent<HTMLInputElement>) => updateLine(index, { quantity: event.target.value })}
              />
            </Grid>
            <Grid item xs={1}>
              <IconButton aria-label="Remove item" disabled={!poValidated} onClick={() => removeLine(index)}>
                <Delete />
              </IconButton>
            </Grid>
          </Grid>
        ))}

        <Stack direction="row" spacing={1} alignItems="center" style={{ marginTop: 8 }}>
          <Button size="small" startIcon={<Add />} disabled={!poValidated} onClick={addLine}>
            Add Item
          </Button>
          <Button size="small" variant="contained" onClick={saveLineItems} disabled={!poValidated || !dirty || saving}>
            {saving ? <CircularProgress size={18} /> : 'Save Items'}
          </Button>
          {!poValidated && (
            <Typography variant="caption" color="text.secondary">
              Look up a PO number to save these items.
            </Typography>
          )}
          {poValidated && dirty && !saving && (
            <Typography variant="caption" color="warning.main">
              Unsaved changes
            </Typography>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export default ListedItemsSection;
